import { EpaperPublicationType, Issue } from '@news-mono/web-common'
import { TheWestSections } from '@west-australian-newspapers/publication-types'
import React, { useRef, useState } from 'react'
import {
    StyledDate,
    StyledDateSpan,
    StyledEpaperCollectionPaperContainer,
    StyledEpaperCollectionPaperMainContainer,
    StyledEpaperCollectionPaperSpanText,
    StyledEpaperCollectionPapersStyledImg,
    StyledEpaperCollectionPaperWrapper,
    StyledIconPremium,
} from './EpaperCollection.styled'

export interface EpaperCollectionProps {
    issues: Issue[]
    sectionToExclude?: EpaperPublicationType
    onCardClick: (link?: string) => void
    border?: boolean
    showMastheadName: boolean
    openEReaderOnCardClick: boolean
    ReadersAlsoLove: boolean
}

export const EpaperCollection: React.FC<EpaperCollectionProps> = (props) => {
    const [anchorElementTabIndex, setAnchorElementTabIndex] = useState(-1)
    const [labelVisibility, setLabelVisibility] = useState<
        'hidden' | 'visible'
    >('hidden')
    const firstEdition = useRef<HTMLAnchorElement>(null)
    const parentContainer = useRef<HTMLInputElement>(null)

    const handleKeyDown = (event: any) => {
        if (
            event.key === 'Enter' &&
            !parentContainer.current?.matches(':focus-within:not(:focus)')
        ) {
            event.preventDefault()
            setAnchorElementTabIndex(0)
            firstEdition.current?.focus()
        }
        if (event.key === 'Escape') {
            setAnchorElementTabIndex(-1)
            parentContainer.current?.focus()
        }
    }
    const handleFocus = () => {
        setLabelVisibility('visible')
    }
    const handleFocusOut = () => {
        setLabelVisibility('hidden')
    }

    let issues = props.issues
    if (props.sectionToExclude)
        issues = issues.filter(
            (issue) => issue.publicationName !== props.sectionToExclude,
        )

    return (
        <div style={{ position: 'relative' }}>
            <label
                style={{
                    visibility: labelVisibility,
                    position: 'absolute',
                    top: '-30px',
                    left: '220px',
                }}
            >
                Press enter to select an edition. Use escape to exit selection.
            </label>
            <StyledEpaperCollectionPaperMainContainer
                aria-label={
                    'Press enter to select an edition, then use the tab key to cycle through the editions. Use escape to exit selecion'
                }
                onKeyDown={handleKeyDown}
                onFocus={handleFocus}
                onBlur={handleFocusOut}
                tabIndex={0}
                ref={parentContainer}
            >
                {issues.map((issue, idx) => {
                    //check index, put a ref on the 1st element, then point to that ref on state change
                    const mastheadMetadata = props.showMastheadName
                        ? getMastheadMetadataFromMastheadName(
                              issue.publicationName,
                          )
                        : undefined

                    const ePaperRedirectUrl =
                        props.ReadersAlsoLove === true
                            ? getMastheadMetadataFromMastheadName(
                                  issue.publicationName,
                              )?.ePaperUrl
                            : issue.url

                    return (
                        issue && (
                            <StyledEpaperCollectionPaperContainer key={idx}>
                                <a
                                    href={ePaperRedirectUrl}
                                    target="_blank"
                                    tabIndex={anchorElementTabIndex}
                                    ref={idx === 0 ? firstEdition : undefined}
                                    onClick={(e) => {
                                        e.preventDefault()
                                        props.onCardClick(ePaperRedirectUrl)
                                    }}
                                    style={{
                                        cursor: 'pointer',
                                        display: 'inline-block',
                                    }}
                                >
                                    <StyledEpaperCollectionPapersStyledImg
                                        src={issue.coverUrl}
                                        onError={({ currentTarget }) => {
                                            currentTarget.onerror = null
                                            currentTarget.src = require('../assets/paper.jpg')
                                        }}
                                        alt={
                                            mastheadMetadata !== undefined
                                                ? `${mastheadMetadata.mastheadName} digital newspaper landing page`
                                                : `${
                                                      getMastheadMetadataFromMastheadName(
                                                          issue.publicationName,
                                                      )?.mastheadName
                                                  } - ${getFormattedDay(
                                                      issue.date,
                                                  )}, ${getFormattedDate(
                                                      issue.date,
                                                  )} edition`
                                        }
                                    />
                                </a>
                                <StyledEpaperCollectionPaperWrapper
                                    aria-hidden={true}
                                >
                                    {mastheadMetadata !== undefined ? (
                                        <>
                                            {issue.isPremium && (
                                                <StyledIconPremium />
                                            )}
                                            <StyledEpaperCollectionPaperSpanText>
                                                {mastheadMetadata.mastheadName}
                                            </StyledEpaperCollectionPaperSpanText>
                                        </>
                                    ) : (
                                        <>
                                            {issue.isPremium && (
                                                <StyledIconPremium />
                                            )}
                                            <StyledEpaperCollectionPaperSpanText>
                                                <FormattedDate
                                                    date={issue.date}
                                                />
                                            </StyledEpaperCollectionPaperSpanText>
                                        </>
                                    )}
                                </StyledEpaperCollectionPaperWrapper>
                            </StyledEpaperCollectionPaperContainer>
                        )
                    )
                })}
            </StyledEpaperCollectionPaperMainContainer>
        </div>
    )
}

const FormattedDate: React.FC<{ date: Date }> = (props) => {
    const day = getFormattedDay(props.date)
    const date = getFormattedDate(props.date)

    return (
        <>
            <StyledDate>
                <StyledDateSpan>{day}, </StyledDateSpan>
                <StyledDateSpan>{date}</StyledDateSpan>
            </StyledDate>
        </>
    )
}

function getFormattedDate(date: Date): string {
    // date formation has been split out like this to avoid strange locale formatting
    const fullDate = `${date.toLocaleString('en-AU', {
        day: 'numeric',
    })} ${date.toLocaleString('en-AU', {
        month: 'long',
    })} ${date.toLocaleString('en-AU', {
        year: 'numeric',
    })}`
    return fullDate
}

function getFormattedDay(date: Date): string {
    const todaysDate = new Date()
    const day =
        todaysDate.getFullYear() - date.getFullYear() === 0 &&
        todaysDate.getMonth() - date.getMonth() === 0 &&
        todaysDate.getDate() - date.getDate() === 0
            ? 'Today'
            : date.toLocaleDateString('en-AU', {
                  weekday: 'long',
              })
    return day
}

function getMastheadMetadataFromMastheadName(masthead: string) {
    type MastheadTypes = TheWestSections & {
        readonly perthnow: 'perthnow'
        readonly thewest: 'thewest'
        readonly 'the-west-australian': 'the-west-australian'
        readonly 'sunday-times': 'sunday-times'
    }
    const mastheadToEPaperUrlMapping: Map<
        keyof MastheadTypes,
        { mastheadName: string; ePaperUrl: string }
    > = new Map([
        [
            'default',
            {
                ePaperUrl: 'https://thewest.com.au/editions',
                mastheadName: 'The West Australian',
            },
        ],
        [
            'the-west-australian',
            {
                ePaperUrl: 'https://thewest.com.au/editions',
                mastheadName: 'The West Australian',
            },
        ],
        [
            'sunday-times',
            {
                ePaperUrl: 'https://thewest.com.au/editions',
                mastheadName: 'Sunday Times',
            },
        ],
        [
            'albany-advertiser',
            {
                ePaperUrl: 'https://www.albanyadvertiser.com.au/editions',
                mastheadName: 'Albany Advertiser',
            },
        ],
        [
            'broome-advertiser',
            {
                ePaperUrl: 'https://www.broomead.com.au/editions',
                mastheadName: 'Broome Advertiser',
            },
        ],
        [
            'bunbury-herald',
            {
                ePaperUrl: 'https://www.bunburyherald.com.au/editions',
                mastheadName: 'Bunbury Herald',
            },
        ],
        [
            'geraldton-guardian',
            {
                ePaperUrl: 'https://www.geraldtonguardian.com.au/editions',
                mastheadName: 'Geraldton Guardian',
            },
        ],
        [
            'great-southern-herald',
            {
                ePaperUrl: 'https://www.gsherald.com.au/editions',
                mastheadName: 'Great Southern Herald',
            },
        ],
        [
            'kalgoorlie-miner',
            {
                ePaperUrl: 'https://www.kalminer.com.au/editions',
                mastheadName: 'Kalgoorlie Miner',
            },
        ],
        [
            'the-kimberley-echo',
            {
                ePaperUrl: 'https://www.kimberleyecho.com.au/editions',
                mastheadName: 'The Kimberley Echo',
            },
        ],
        [
            'manjimup-bridgetown-times',
            {
                ePaperUrl: 'https://www.mbtimes.com.au/editions',
                mastheadName: 'Manjimup-Bridgetown Times',
            },
        ],
        [
            'midwest-times',
            {
                ePaperUrl: 'https://www.midwesttimes.com.au/editions',
                mastheadName: 'Midwest Times',
            },
        ],
        [
            'narrogin-observer',
            {
                ePaperUrl: 'https://www.narroginobserver.com.au/editions',
                mastheadName: 'Narrogin Observer',
            },
        ],
        [
            'pilbara-news',
            {
                ePaperUrl: 'https://www.pilbaranews.com.au/editions',
                mastheadName: 'Pilbara News',
            },
        ],
        [
            'sound-telegraph',
            {
                ePaperUrl: 'https://www.soundtelegraph.com.au/editions',
                mastheadName: 'Sound Telegraph',
            },
        ],
        [
            'south-western-times',
            {
                ePaperUrl: 'https://www.swtimes.com.au/editions',
                mastheadName: 'South Western Times',
            },
        ],
        [
            'busselton-dunsborough-times',
            {
                ePaperUrl: 'https://www.bdtimes.com.au/editions',
                mastheadName: 'Busselton Dunsborough Times',
            },
        ],
        [
            'countryman',
            {
                ePaperUrl: 'https://www.countryman.com.au/editions',
                mastheadName: 'Countryman',
            },
        ],
        [
            'augusta-margaret-river-times',
            {
                ePaperUrl: 'https://www.amrtimes.com.au/editions',
                mastheadName: 'Augusta Margaret River Times',
            },
        ],
        [
            'harvey-waroona-reporter',
            {
                ePaperUrl: 'https://www.harveyreporter.com.au/editions',
                mastheadName: 'Harvey-Waroona Reporter',
            },
        ],
        [
            'north-west-telegraph',
            {
                ePaperUrl: 'https://www.northwesttelegraph.com.au/editions',
                mastheadName: 'North West Telegraph',
            },
        ],
        [
            'perthnow',
            {
                ePaperUrl: 'https://www.perthnow.com.au/editions',
                mastheadName: 'PerthNow',
            },
        ],
    ])

    return mastheadToEPaperUrlMapping.get(masthead as keyof MastheadTypes)
}
